import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, Clock, Award, Users, TrendingUp, ArrowRight, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useContactPopup } from '@/hooks/useContactPopup';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { getGeoLocation, submitLead } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { countryCodes } from '@/data/countryCodes';

const HeroForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+44',
    phone: '',
    message: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const result = await getGeoLocation();
      if (result.success) {
        setGeoData(result.data);
      }
    };
    fetchGeoLocation();
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: digitsOnly });
    
    if (digitsOnly && !validatePhone(digitsOnly)) {
      setErrors({ ...errors, phone: 'Phone number must be exactly 10 digits' });
    } else {
      setErrors({ ...errors, phone: '' });
    }
  };

  const handleEmailChange = (value) => {
    setFormData({ ...formData, email: value });
    
    if (value && !validateEmail(value)) {
      setErrors({ ...errors, email: 'Please enter a valid email address (e.g., user@example.com)' });
    } else {
      setErrors({ ...errors, email: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    const newErrors = {};
    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await submitLead(formData, geoData);
      
      if (result.success) {
        toast.success(result.message || 'Thank you! We\'ll contact you within 24 hours.');
        setFormData({ name: '', email: '', countryCode: '+44', phone: '', message: '' });
        setErrors({ email: '', phone: '' });
        navigate(`/application/main?customerid=${result.customerId}`);
      } else {
        toast.error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="p-8 border-2 border-primary shadow-elegant-xl sticky top-24">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">Secure Your Trademark - Only £350 Yearly</h2>
        <p className="text-lg text-muted-foreground">Fill out the form and we'll contact you within 24 hours</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="hero-name">Name *</Label>
          <Input
            id="hero-name"
            placeholder="John Smith"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="hero-email">Email *</Label>
          <Input
            id="hero-email"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={(e) => handleEmailChange(e.target.value)}
            required
            className={errors.email ? 'border-red-500' : ''}
          />
          {errors.email && (
            <p className="text-sm text-red-500">{errors.email}</p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="hero-phone">Contact Number *</Label>
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.code} ({item.country})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1 space-y-2">
              <Input
                id="hero-phone"
                type="tel"
                placeholder="1234567890 (10 digits)"
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                required
                maxLength={10}
                className={errors.phone ? 'border-red-500' : ''}
              />
              {errors.phone && (
                <p className="text-sm text-red-500">{errors.phone}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="hero-message">Description</Label>
          <Textarea
            id="hero-message"
            placeholder="Tell us about your trademark needs..."
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            rows={3}
          />
        </div>
        
        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full bg-primary hover:bg-primary-dark text-lg py-6 disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : 'Get Started Now'}
          {!isSubmitting && <ArrowRight className="ml-2 w-5 h-5" />}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          By submitting, you agree to our Terms of Service
        </p>
      </form>
    </Card>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ testimonial }) => (
  <Card className="p-6 h-full flex flex-col border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
    {/* Star Rating */}
    <div className="flex gap-1 mb-4">
      {[1, 2, 3, 4, 5].map((star) => {
        const rating = testimonial.rating;
        const isFullStar = star <= rating;
        const isHalfStar = star === Math.ceil(rating) && rating % 1 !== 0;
        
        return (
          <div key={star} className="relative w-5 h-5">
            {isHalfStar ? (
              <>
                <Star className="w-5 h-5 text-gray-300 absolute inset-0" fill="currentColor" />
                <div className="overflow-hidden absolute w-2.5 h-5">
                  <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                </div>
              </>
            ) : (
              <Star 
                className={`w-5 h-5 ${isFullStar ? 'text-yellow-400' : 'text-gray-300'}`} 
                fill="currentColor" 
              />
            )}
          </div>
        );
      })}
    </div>
    
    <p className="text-base text-foreground mb-6 leading-relaxed italic flex-1">
      &quot;{testimonial.content}&quot;
    </p>
    
    <div className="border-t border-border pt-4">
      <p className="font-semibold text-foreground">{testimonial.name}</p>
      <p className="text-sm text-muted-foreground mt-1">{testimonial.role}</p>
    </div>
  </Card>
);

export const HomePage = () => {
  const { openPopup } = useContactPopup();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  // Function to get visible testimonials based on viewport
  const getVisibleTestimonials = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const testimonialsPerView = isMobile ? 1 : 3;
    
    const visible = [];
    for (let i = 0; i < testimonialsPerView; i++) {
      const index = (currentTestimonial + i) % testimonials.length;
      visible.push({ ...testimonials[index], originalIndex: index });
    }
    return visible;
  };

  const nextTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev + step) % testimonials.length);
  };

  const prevTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev - step + testimonials.length) % testimonials.length);
  };

  // TestimonialCard component moved outside HomePage

  const steps = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Information Collection',
      description: 'We gather your business details and trademark specifications through our comprehensive consultation process today'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Research and Search Report',
      description: 'Our experts conduct thorough UK IPO database searches identifying potential conflicts before filing your application'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Application and Submission',
      description: 'We prepare, review, and submit your application while monitoring progress until successful trademark registration completion'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Founder, TechFlow Solutions',
      content: 'Trademark Assist made registration effortless. Professional handling from search to approval. Certificate received in just 5 months.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1627161683077-e34782c24d81?w=128&h=128&fit=crop&q=80'
    },
    {
      name: 'James Robertson',
      role: 'Creative Director, Pixel Studios',
      content: 'Excellent service for copyright registration. Clear explanations and transparent pricing. They\'re our go-to for all IP matters.',
      rating: 4.5,
      image: 'https://images.unsplash.com/photo-1642257859842-c95f9fa8121d?w=128&h=128&fit=crop&q=80'
    },
    {
      name: 'Priya Sharma',
      role: 'Marketing Director, GreenLeaf',
      content: 'Comprehensive search uncovered a conflict we missed. Saved us from legal issues and helped us choose a stronger brand name.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=128&h=128&fit=crop&q=80'
    },
    {
      name: 'Michael Chen',
      role: 'Co-Founder, UrbanBuild Ltd',
      content: 'Company formation made seamless. All paperwork handled efficiently with incorporation certificate delivered within 24 hours.',
      rating: 4,
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=128&h=128&fit=crop&q=80'
    },
    {
      name: 'Emma Thompson',
      role: 'E-commerce Manager, Heritage Goods',
      content: 'Perfect guidance for Amazon Brand Registry. Our sales increased 40% in three months thanks to added trust and protection.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1581065178047-8ee15951ede6?w=128&h=128&fit=crop&q=80'
    },
    {
      name: 'David Williams',
      role: 'Director, Elite Fashion Group',
      content: 'Expert guidance on multi-jurisdiction filings. Helped us navigate EU and UK trademark law complexities post-Brexit.',
      rating: 4.5,
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=128&h=128&fit=crop&q=80'
    }
  ];

  const features = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Legal Protection',
      description: 'Secure exclusive rights to your brand name, logo, and identity across the UK.'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Fast Processing',
      description: 'Expert handling ensures quick application processing and minimal delays.'
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Expert Guidance',
      description: 'Professional support from experienced trademark attorneys throughout the process.'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: '24/7 Support',
      description: 'Round-the-clock customer support to answer all your trademark questions.'
    }
  ];

  const stats = [
    { number: '10,000+', label: 'Trademarks Registered' },
    { number: '98%', label: 'Success Rate' },
    { number: '24/7', label: 'Expert Support' },
    { number: '15+', label: 'Years Experience' }
  ];

  useEffect(() => {
    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 4000);
    
    const testimonialInterval = setInterval(() => {
      nextTestimonial();
    }, 5000); // Smooth auto-slide every 5 seconds

    return () => {
      clearInterval(stepInterval);
      clearInterval(testimonialInterval);
    };
  }, []); // Remove dependency to prevent timer restart

  return (
    <div className="min-h-screen">
      {/* Hero Section with Animated UK Background */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Animated UK Background with Famous Landmark */}
        <div className="absolute inset-0 z-0">
          {/* UK Landmark Background - Big Ben */}
          <img
            src="https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&h=1080&fit=crop"
            alt="London Big Ben UK Landmark"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          {/* Animated UK Flag GIF - Larger and More Visible */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <img
              src="https://www.3dflagsplus.com/gifs/United_Kingdom_240x240.gif"
              alt="UK Flag Animation"
              className="w-64 h-64 opacity-25"
            />
          </div>
          {/* Gradient Overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-background/85 via-background/80 to-secondary/70"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Right Form - Shows First on Mobile */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <HeroForm />
            </motion.div>

            {/* Left Content - Shows Second on Mobile */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <Badge className="mb-4 bg-primary text-primary-foreground">UK's Most Trusted Trademark Registration Service Since 2009</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Protect Your Brand Identity with Confidence
              </h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Your business deserves ironclad protection. In today's competitive marketplace, securing your trademark isn't just smart—it's essential. Our expert team handles everything from comprehensive searches to IPO filing, ensuring your brand remains exclusively yours. With a 98% success rate and over 10,000 trademarks registered, we transform complex legal processes into straightforward solutions.
              </p>
              
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <div className="text-3xl font-bold text-primary">{stat.number}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why You Need This Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Image - Shows First on Mobile */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1"
            >
              <div className="rounded-2xl overflow-hidden shadow-elegant-xl">
                <img
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop"
                  alt="Business Protection"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
              </div>
            </motion.div>

            {/* Right Content - Shows Second on Mobile */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-2"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Why Trademark Registration Is Non-Negotiable for Your Business
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Without a registered trademark, your brand exists in legal limbo—vulnerable to copycats, counterfeiters, and competitors who can legally adopt similar names or logos. Trademark registration grants you exclusive rights across the UK, enabling legal recourse against infringement, increasing your company's valuation, and building customer trust. It's not just paperwork; it's your business insurance policy. The moment you start trading without protection is the moment you risk everything you've built.
              </p>
              <Button onClick={openPopup} size="lg" className="bg-primary hover:bg-primary-dark">
                Get Protected Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work - 3-Step Process */}
      <section className="py-20 px-4 bg-gradient-to-br from-accent/30 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Our 3-Step Process
            </h2>
            <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              We've streamlined trademark registration into a transparent, stress-free process. From initial consultation to official registration, our experienced team handles every detail with precision and care. No hidden fees, no confusing jargon—just expert guidance every step of the way.
            </p>
          </div>

          {/* Desktop View */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 text-center hover:shadow-elegant-lg transition-all duration-300 border-border h-full flex flex-col">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-foreground mb-3">Information Collection</h3>
                  <p className="text-muted-foreground">We gather your business details and trademark specifications through our comprehensive consultation process today</p>
                </div>
                <div className="mt-6">
                  <Badge variant="outline" className="text-primary border-primary">Step 1</Badge>
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-8 text-center hover:shadow-elegant-lg transition-all duration-300 border-border h-full flex flex-col">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-foreground mb-3">Research and Search Report</h3>
                  <p className="text-muted-foreground">Our experts conduct thorough UK IPO database searches identifying potential conflicts before filing your application</p>
                </div>
                <div className="mt-6">
                  <Badge variant="outline" className="text-primary border-primary">Step 2</Badge>
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Card className="p-8 text-center hover:shadow-elegant-lg transition-all duration-300 border-border h-full flex flex-col">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-foreground mb-3">Application and Submission</h3>
                  <p className="text-muted-foreground">We prepare, review, and submit your application while monitoring progress until successful trademark registration completion</p>
                </div>
                <div className="mt-6">
                  <Badge variant="outline" className="text-primary border-primary">Step 3</Badge>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Mobile Slider */}
          <div className="md:hidden">
            <Card className="p-8 text-center border-border min-h-[350px] flex flex-col">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-6">
                {steps[currentStep].icon}
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-semibold text-foreground mb-3">{steps[currentStep].title}</h3>
                <p className="text-muted-foreground">{steps[currentStep].description}</p>
              </div>
              <div className="mt-6 flex flex-col items-center gap-3">
                <Badge variant="outline" className="text-primary border-primary">Step {currentStep + 1} of 3</Badge>
                <div className="flex gap-2">
                  {steps.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentStep ? 'bg-primary w-8' : 'bg-border'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Transparent Pricing for Every Business Need
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              No surprises, no hidden costs. Choose the package that fits your business stage and budget. Every tier includes expert support, comprehensive guidance, and our commitment to your brand's protection.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            {/* Trademark Watch */}
            <Card className="p-6 border-2 border-border hover:border-primary hover:shadow-glow transition-all duration-500 flex flex-col relative overflow-hidden group bg-gradient-to-br from-background to-accent/30">
              {/* Fill hover effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">Trademark Watch</h3>
                <div className="text-3xl font-bold text-primary mb-4 group-hover:scale-110 transition-transform duration-300">£350<span className="text-base text-muted-foreground">/year</span></div>
              </div>
              <div className="space-y-3 mb-6 flex-1 overflow-y-auto max-h-[250px] custom-scrollbar relative z-10">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Ongoing trademark monitoring and protection</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Case review and filing assistance</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Basic trademark search</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Online filing support</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Trademark security assurance</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">24/7 monitoring for infringements</span>
                </div>
              </div>
              <Button onClick={openPopup} variant="outline" className="w-full mt-auto border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-lg transition-all duration-300 relative z-10">
                Start Monitoring
              </Button>
            </Card>

            {/* Basic Registration */}
            <Card className="p-6 border-2 border-border hover:border-secondary hover:shadow-glow-red transition-all duration-500 flex flex-col relative overflow-hidden group bg-gradient-to-br from-background to-accent/30">
              {/* Fill hover effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-foreground mb-2 group-hover:text-secondary transition-colors duration-300">Basic Registration</h3>
                <div className="text-3xl font-bold text-secondary mb-1 group-hover:scale-110 transition-transform duration-300">from £299</div>
                <div className="text-sm text-muted-foreground mb-4">+ £170 IPO Govt Fee</div>
              </div>
              <div className="space-y-3 mb-6 flex-1 overflow-y-auto max-h-[250px] custom-scrollbar relative z-10">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Complete case review and filing</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Direct hit trademark search</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Trademark alert system</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">High satisfaction guarantee</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Refusal risk assessment</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground font-semibold">Money-back guarantee</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Dedicated case manager</span>
                </div>
              </div>
              <Button onClick={openPopup} className="w-full mt-auto bg-secondary hover:bg-secondary-light hover:shadow-lg transition-all duration-300 relative z-10">
                Choose Basic
              </Button>
            </Card>

            {/* Premium Registration */}
            <Card className="p-6 border-4 border-primary relative hover:shadow-2xl hover:scale-105 transition-all duration-500 flex flex-col overflow-visible group bg-gradient-to-br from-primary/5 via-background to-secondary/5">
              <Badge className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-lg animate-pulse z-10">⭐ Most Popular</Badge>
              
              {/* Fill hover effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">Premium Registration</h3>
                <div className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-1 group-hover:scale-110 transition-transform duration-300">from £499</div>
                <div className="text-sm text-muted-foreground mb-4">+ £170 IPO Govt Fee</div>
              </div>
              <div className="space-y-3 mb-6 flex-1 overflow-y-auto max-h-[250px] custom-scrollbar relative z-10">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground font-semibold">Everything in Basic package</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Comprehensive search report</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">High priority application handling</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">95%+ approval rate</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Cease & desist letter drafting</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground font-semibold">Free 30-minute consultation</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Expedited processing</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Office action response assistance</span>
                </div>
              </div>
              <Button onClick={openPopup} className="w-full mt-auto bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300 relative z-10">
                Choose Premium
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center text-center"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Why Choose Trademark Assist
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                In the crowded marketplace of trademark services, we stand apart through proven expertise, unwavering client commitment, and results that speak for themselves. Our team combines legal precision with business understanding, ensuring your trademark registration isn't just successful—it's strategic.
              </p>

              <div className="space-y-6 flex-1 w-full">
                {/* USP 1 */}
                <div className="flex flex-col gap-4 group items-center text-center">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-primary/20 group-hover:from-primary group-hover:to-primary-light rounded-xl flex items-center justify-center text-primary group-hover:text-primary-foreground transition-all duration-300">
                      <TrendingUp className="w-7 h-7" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      Unmatched Success Rate
                    </h3>
                    <h4 className="text-base font-semibold text-primary mb-2">
                      98% Approval Rate That Industry Leaders Envy
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">
                      While other services struggle with rejection rates, our meticulous process achieves a 98% approval rate—far exceeding industry standards. This isn't luck; it's the result of comprehensive pre-filing searches, expert application preparation, and deep understanding of IPO requirements.
                    </p>
                  </div>
                </div>

                {/* USP 2 */}
                <div className="flex flex-col gap-4 group items-center text-center">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-secondary/10 to-secondary/20 group-hover:from-secondary group-hover:to-secondary-light rounded-xl flex items-center justify-center text-secondary group-hover:text-secondary-foreground transition-all duration-300">
                      <Users className="w-7 h-7" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-2 group-hover:text-secondary transition-colors">
                      Expert Legal Team
                    </h3>
                    <h4 className="text-base font-semibold text-secondary mb-2">
                      Trademark Attorneys with 15+ Years Protecting UK Businesses
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">
                      Our team isn't just legally qualified—they're battle-tested professionals who've navigated thousands of trademark registrations, disputes, and complex IP scenarios. With over 15 years of combined experience, we understand the nuances that make or break applications.
                    </p>
                  </div>
                </div>

                {/* USP 3 */}
                <div className="flex flex-col gap-4 group items-center text-center">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary/10 via-secondary/10 to-primary/20 group-hover:from-primary group-hover:via-secondary group-hover:to-primary-light rounded-xl flex items-center justify-center text-primary group-hover:text-primary-foreground transition-all duration-300">
                      <Clock className="w-7 h-7" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      24/7 Support
                    </h3>
                    <h4 className="text-base font-semibold text-primary mb-2">
                      Never Navigate Trademark Complexities Alone Again
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">
                      From your first inquiry to final registration and beyond, we're with you every step of the way. Our 24/7 support means you're never left wondering about application status or next steps. We handle IPO communications, respond to office actions, and keep you informed through every stage.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Image - Full Height */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-full min-h-[600px]"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-elegant-xl h-full">
                <img
                  src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=900&fit=crop"
                  alt="Professional Trademark Services"
                  className="w-full h-full object-cover"
                />
                {/* Decorative overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent"></div>
              </div>
              
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br from-secondary/20 to-primary/20 rounded-full blur-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Stats Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Our Track Record</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Numbers That Prove Our Excellence
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full"></div>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-8 text-center border-2 border-transparent hover:border-primary/30 hover:shadow-elegant-xl transition-all duration-300 bg-gradient-to-br from-card to-primary/5 relative overflow-hidden group">
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-primary/10 rounded-bl-full group-hover:scale-150 transition-transform duration-500"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300 shadow-lg">
                    <TrendingUp className="w-8 h-8 text-primary-foreground" />
                  </div>
                  <div className="text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-3">
                    10,000+
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-3">Trademarks Successfully Registered</h3>
                  <div className="h-1 w-16 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full mb-4"></div>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Over a decade of dedicated service has resulted in more than 10,000 trademarks protected across every industry imaginable. Each registration represents a business that trusted us with their most valuable asset, and we delivered.
                  </p>
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="p-8 text-center border-2 border-transparent hover:border-secondary/30 hover:shadow-elegant-xl transition-all duration-300 bg-gradient-to-br from-card to-secondary/5 relative overflow-hidden group">
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-secondary/10 rounded-bl-full group-hover:scale-150 transition-transform duration-500"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-secondary to-secondary-light rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300 shadow-lg">
                    <Award className="w-8 h-8 text-secondary-foreground" />
                  </div>
                  <div className="text-5xl font-bold bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent mb-3">
                    98%
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-3">Application Success Rate</h3>
                  <div className="h-1 w-16 bg-gradient-to-r from-secondary to-primary mx-auto rounded-full mb-4"></div>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Our near-perfect approval rate isn't accidental—it's the direct result of rigorous pre-filing searches, expert legal review, and meticulous application preparation. While industry average hovers around 75%, our 98% success rate proves our methodology works.
                  </p>
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="p-8 text-center border-2 border-transparent hover:border-primary/30 hover:shadow-elegant-xl transition-all duration-300 bg-gradient-to-br from-card to-primary/5 relative overflow-hidden group">
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-primary/10 rounded-bl-full group-hover:scale-150 transition-transform duration-500"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-warning to-warning/80 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300 shadow-lg">
                    <Users className="w-8 h-8 text-warning-foreground" />
                  </div>
                  <div className="text-5xl font-bold bg-gradient-to-r from-warning to-primary bg-clip-text text-transparent mb-3">
                    4.9/5
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-3">Client Satisfaction Rating</h3>
                  <div className="h-1 w-16 bg-gradient-to-r from-warning to-primary mx-auto rounded-full mb-4"></div>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Based on over 2,500 verified client reviews, our 4.9-star rating reflects consistent excellence in service delivery, communication, and results. Our clients praise our transparency, responsiveness, and ability to explain complex legal processes in understandable terms.
                  </p>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Additional Visual Element */}
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-muted-foreground text-lg mb-6">Trusted by businesses across the United Kingdom</p>
            <div className="flex justify-center items-center gap-4 flex-wrap">
              <Badge variant="outline" className="px-4 py-2 text-sm border-primary/30">Startups</Badge>
              <Badge variant="outline" className="px-4 py-2 text-sm border-primary/30">SMEs</Badge>
              <Badge variant="outline" className="px-4 py-2 text-sm border-primary/30">Enterprises</Badge>
              <Badge variant="outline" className="px-4 py-2 text-sm border-primary/30">Creative Agencies</Badge>
              <Badge variant="outline" className="px-4 py-2 text-sm border-primary/30">E-commerce</Badge>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mid-Page CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1920&h=800&fit=crop"
            alt="Brand Protection"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary-dark/90 to-primary/80"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-primary-foreground"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Don't Let Competitors Steal Your Brand Identity
              </h2>
              
              <p className="text-lg text-primary-foreground/90 leading-relaxed mb-8">
                Every day without trademark protection is a day your brand remains vulnerable. Competitors can adopt similar names, counterfeiters can damage your reputation, and you have limited legal recourse. Don't wait until it's too late. Our streamlined process gets your application filed within 48 hours, and our expert team handles every detail from search to registration. Take the first step toward complete brand protection—your future business growth depends on the decisions you make today.
              </p>

              <Button 
                onClick={openPopup} 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-6 bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:scale-105 transition-all duration-300 shadow-elegant-xl"
              >
                Start My Trademark Registration Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials - Center Focus with Blur Effect */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#012169]/10 via-background to-[#C8102E]/10 relative overflow-hidden">
        {/* UK Theme Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#012169] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#C8102E] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-gradient-to-r from-[#012169] to-[#C8102E] text-white">Client Success Stories</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                What Our Clients Say About Protecting Their Brands
              </h2>
              <p className="text-lg text-muted-foreground">Real experiences from businesses we've helped protect</p>
            </motion.div>
          </div>

          <div className="relative">
            {/* Left Arrow */}
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-20 bg-gradient-to-r from-[#012169] to-[#C8102E] text-white rounded-full p-3 shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300"
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Right Arrow */}
            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-20 bg-gradient-to-r from-[#012169] to-[#C8102E] text-white rounded-full p-3 shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300"
              aria-label="Next testimonials"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Testimonials Grid with Center Focus */}
            <div className="overflow-hidden px-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ 
                    duration: 0.5,
                    ease: "easeInOut"
                  }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center"
                >
                  {getVisibleTestimonials().map((testimonial, index) => {
                    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
                    const isCenterCard = isMobile ? index === 0 : index === 1;
                    
                    return (
                    <div 
                      key={`${testimonial.originalIndex}-${index}`}
                      className={`transition-all duration-500 ${
                        isCenterCard ? 'md:z-10 scale-105' : 'md:scale-95'
                      }`}
                      style={{
                        opacity: isMobile ? 1 : (isCenterCard ? 1 : 0.6),
                        filter: isMobile ? 'blur(0px)' : (isCenterCard ? 'blur(0px)' : 'blur(3px)')
                      }}
                    >
                      <Card className={`p-8 h-full flex flex-col border-2 transition-all duration-500 relative ${
                        isCenterCard
                          ? 'border-[#012169] bg-white shadow-2xl'
                          : 'border-border bg-white/80'
                      }`}>
                        {/* Profile Image */}
                        <div className="flex items-center gap-4 mb-6">
                          <div 
                            className={`relative w-16 h-16 rounded-full overflow-hidden border-4 ${
                              isCenterCard 
                                ? 'border-[#012169]' 
                                : 'border-gray-300'
                            }`}
                          >
                            <img 
                              src={testimonial.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=012169&color=fff&size=128`}
                              alt={testimonial.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="font-bold text-foreground text-lg">{testimonial.name}</p>
                            <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                          </div>
                        </div>

                        {/* Star Rating */}
                        <div className="flex items-center gap-1 mb-4">
                          {Array.from({ length: 5 }).map((_, star) => {
                            const rating = testimonial.rating;
                            const isFullStar = star < Math.floor(rating);
                            const isHalfStar = star === Math.floor(rating) && rating % 1 !== 0;
                            
                            return (
                              <div 
                                key={star} 
                                className="relative w-5 h-5"
                              >
                                {isHalfStar ? (
                                  <>
                                    <Star className="w-5 h-5 text-gray-300 absolute inset-0" fill="currentColor" />
                                    <div className="overflow-hidden absolute w-2.5 h-5">
                                      <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                                    </div>
                                  </>
                                ) : (
                                  <Star 
                                    className={`w-5 h-5 ${isFullStar ? 'text-yellow-400' : 'text-gray-300'}`} 
                                    fill="currentColor" 
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Testimonial Content */}
                        <p 
                          className="text-base text-foreground mb-6 leading-relaxed italic flex-1"
                        >
                          &quot;{testimonial.content}&quot;
                        </p>
                        
                        {/* UK Theme Accent */}
                        {isCenterCard && (
                          <div 
                            className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#012169] to-[#C8102E] opacity-10 rounded-bl-full"
                          ></div>
                        )}
                      </Card>
                    </div>
                  )})}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 mt-12">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index * 3)}
                  className={`h-3 rounded-full transition-all duration-500 ease-out ${
                    Math.floor(currentTestimonial / 3) === index
                      ? 'bg-gradient-to-r from-[#012169] to-[#C8102E] w-12 shadow-lg' 
                      : 'bg-border w-3 hover:bg-primary/50 hover:w-6'
                  }`}
                  aria-label={`View testimonial group ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Frequently Asked Questions About Our Services
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-0" className="border border-border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left text-foreground font-semibold hover:text-primary">
                How long does the complete trademark registration process take?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                From filing to official registration, the UK trademark process typically takes 4-6 months, assuming no objections or oppositions. This includes the IPO's examination period (approximately 2-3 months) and the publication period for potential oppositions (2 months). We monitor your application throughout and handle any office actions promptly. With our premium service, we expedite wherever possible to minimize delays. However, timelines can extend to 9-12 months if the IPO raises objections or if third parties file oppositions, which we'll navigate on your behalf.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-1" className="border border-border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left text-foreground font-semibold hover:text-primary">
                What's the difference between your service packages, and which should I choose?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Our Trademark Watch (£350/year) is ideal for ongoing monitoring of existing trademarks. Basic Registration (£299 + govt fees) suits straightforward applications with common business names and includes essential search and filing services. Premium Registration (£499 + govt fees) offers comprehensive searching, higher priority handling, and includes consultation and legal support—recommended for unique brands, competitive industries, or if you want maximum approval chances. Most clients choose Premium for the peace of mind and superior success rate, but we'll assess your specific needs during consultation to recommend the best fit.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left text-foreground font-semibold hover:text-primary">
                What happens if my trademark application is rejected by the IPO?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Rejection is rare with our 98% success rate, but if it occurs, we immediately analyze the IPO's objections and develop a response strategy. For Basic and Premium package clients, we provide guidance on responding to office actions. Premium clients receive hands-on assistance crafting responses, amending applications, or filing appeals. In some cases, minor modifications resolve objections; in others, we may recommend alternative approaches. Our money-back guarantee (on applicable packages) ensures you're protected financially if we cannot overcome rejections despite our best efforts. We never leave clients stranded mid-process.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left text-foreground font-semibold hover:text-primary">
                Can you help protect my trademark internationally, beyond the UK?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Yes, absolutely. While our core service focuses on UK IPO registrations, we have partnerships with IP firms worldwide and can facilitate international trademark protection through the Madrid Protocol system, which covers 130+ countries. We also handle EU trademark applications (covering all 27 EU member states) and direct country-specific filings. During your consultation, we'll assess your international expansion plans and recommend the most cost-effective protection strategy. International trademark costs vary by territory but typically start from £800-1,200 per country or region, plus government fees.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left text-foreground font-semibold hover:text-primary">
                Do I really need professional help, or can I file a trademark application myself?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                While DIY filing is technically possible, it's risky without legal expertise. Over 40% of self-filed applications are rejected due to inadequate searches, incorrect classifications, or improper descriptions. Rejected applications waste time and money—you lose government fees (non-refundable) and must start over. Our service eliminates this risk through comprehensive pre-filing searches that identify conflicts you'd likely miss, proper classification expertise (critical for protection scope), and application preparation that meets IPO standards. We also handle complex responses to office actions, which confuse most non-lawyers. The cost of rejection far exceeds our service fees, making professional assistance a wise investment in your brand's future.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
